import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, ExternalLink, KeyRound, MessageCircle } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { api, ApiError } from '../services/api'
import type { StampLotteryKeywordResult, StampLotterySummary } from '../services/api'

/**
 * #2490 LINE 公式アカウントの友だち追加を条件にした参加抽選の入口。
 *
 * 開催者があいさつメッセージ (友だち追加の直後に自動で届く) に合言葉を書き、客はここで
 * それを入れる。**LINE 側の開発も、開催者の鍵を当社が預かることも要らない** 方式。
 *
 * 1. 友だち追加のボタン (開催者が設定したリンク)
 * 2. 届いた合言葉の入力欄
 *
 * 合言葉を入れても**スタンプが 1 個も無ければ引けない** (合言葉は会場に来なくても
 * 手に入るため)。その場合は「スタンプを 1 個集めると引ける」と出し、最初のスキャンで引く。
 *
 * 入力はアカウントだけ (抽選はアカウント登録への対価)。ゲストには隣に出る
 * GuestUpgradeCard がログインを勧めるので、ここでは手順の 1 だけを見せる。
 */
export default function LotteryKeywordCard({
  eventId,
  lottery,
  keywordEntered,
  hasStamp,
  isLoggedIn,
  isGuest,
  redirectTo,
  onResult,
}: {
  eventId: string
  lottery: StampLotterySummary
  /** 合言葉を入れたか (サーバの値)。ログインしていなければ null / undefined */
  keywordEntered: boolean | null | undefined
  hasStamp: boolean
  isLoggedIn: boolean
  isGuest: boolean
  /** ログイン後に戻す場所 */
  redirectTo: string
  onResult: (result: StampLotteryKeywordResult) => void
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [keyword, setKeyword] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // サーバは合言葉の状態 (true / false) を**アカウントにだけ**返す。返っていればログイン済み。
  // 再読み込みの直後は端末側のログイン状態の復元が遅れ、一瞬「アカウントが必要です」が出ていた
  const signedIn = isLoggedIn || typeof keywordEntered === 'boolean'
  // リンクは運営画面で https だけを受け付けているが、表示側でも確かめる (スキームの注入を通さない)
  const addUrl = lottery.lineAddUrl && lottery.lineAddUrl.startsWith('https://') ? lottery.lineAddUrl : ''

  const submit = async () => {
    const typed = keyword.trim()
    if (!typed || sending) return
    setSending(true)
    setError(null)
    try {
      const result = await api.enterStampLotteryKeyword(eventId, typed)
      setKeyword('')
      onResult(result)
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0
      setError(
        status === 400
          ? t('stampRally.keywordWrong')
          : status === 429
            ? t('stampRally.keywordLocked')
            : status === 410
              ? t('stampRally.keywordClosed')
              : t('stampRally.keywordFailed'),
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="rounded-2xl bg-white px-4 py-4 shadow-card" data-testid="lottery-keyword-card">
      <div className="flex items-start gap-2">
        <MessageCircle size={18} className="mt-0.5 shrink-0 text-[#06C755]" />
        <div className="min-w-0">
          <p className="font-display text-sm font-semibold text-text-primary">{t('stampRally.keywordTitle')}</p>
          <p className="mt-0.5 font-body text-xs leading-relaxed text-text-secondary">{t('stampRally.keywordNote')}</p>
        </div>
      </div>

      {/* 1. 友だち追加 */}
      <p className="mt-3 font-body text-[11px] font-medium text-text-tertiary">{t('stampRally.keywordStep1')}</p>
      {addUrl ? (
        <a
          href={addUrl}
          target="_blank"
          rel="noopener noreferrer"
          // アカウント名が長いと 1 行に収まらない。高さを固定せず、折り返しても枠の内側に収める
          className="tap-effect mt-1 flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-[#06C755] px-4 py-2 text-center font-display text-sm font-semibold leading-snug text-white"
        >
          {lottery.lineAccountName
            ? t('stampRally.keywordAddNamed', { name: lottery.lineAccountName })
            : t('stampRally.keywordAdd')}
          <ExternalLink size={14} className="shrink-0" />
        </a>
      ) : (
        lottery.lineAccountName && (
          <p className="mt-1 font-body text-sm text-text-primary">
            {t('stampRally.keywordAddNamed', { name: lottery.lineAccountName })}
          </p>
        )
      )}

      {/* 2. 合言葉 */}
      <p className="mt-3 font-body text-[11px] font-medium text-text-tertiary">{t('stampRally.keywordStep2')}</p>
      {!signedIn ? (
        <>
          <p className="mt-1 font-body text-xs leading-relaxed text-text-secondary">
            {t('stampRally.keywordLoginNeeded')}
          </p>
          {/* ゲストには GuestUpgradeCard が同じボタンを出すので、ここでは重ねない */}
          {!isGuest && (
            <button
              onClick={() => {
                sessionStorage.setItem('postLoginRedirect', redirectTo)
                navigate('/login')
              }}
              className="tap-effect mt-2 h-11 w-full rounded-xl bg-navy font-display text-sm font-semibold text-white"
            >
              {t('stampRally.keywordLogin')}
            </button>
          )}
        </>
      ) : keywordEntered ? (
        <div className="mt-1 rounded-xl bg-matcha/10 px-3 py-2.5">
          <p className="flex items-center gap-1.5 font-body text-sm font-medium text-text-primary">
            <CheckCircle2 size={16} className="shrink-0 text-matcha" />
            {t('stampRally.keywordAccepted')}
          </p>
          {!hasStamp && (
            <p className="mt-1 font-body text-xs leading-relaxed text-text-secondary">
              {t('stampRally.keywordNeedsStamp')}
            </p>
          )}
        </div>
      ) : (
        <form
          className="mt-1 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <label className="relative min-w-0 flex-1">
            <KeyRound size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t('stampRally.keywordPlaceholder')}
              aria-label={t('stampRally.keywordPlaceholder')}
              maxLength={100}
              autoComplete="off"
              enterKeyHint="send"
              className="h-11 w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 font-body text-sm text-text-primary focus:border-navy focus:outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={sending || !keyword.trim()}
            className="tap-effect h-11 shrink-0 rounded-xl bg-yokoso-red px-4 font-display text-sm font-semibold text-white disabled:opacity-50"
          >
            {sending ? t('stampRally.keywordSending') : t('stampRally.keywordSubmit')}
          </button>
        </form>
      )}
      {error && (
        <p role="alert" className="mt-2 font-body text-xs leading-relaxed text-yokoso-red">
          {error}
        </p>
      )}
    </div>
  )
}
